import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, filter, map, mergeMap, Observable, of, retry, Subscription, tap } from 'rxjs';
import { MessageService } from './message.service';
import { ConfigLoaderService } from '../config/configloader.service';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { AudioConfig, SpeechConfig, SpeechRecognizer, PhraseListGrammar } from 'microsoft-cognitiveservices-speech-sdk';
import { PersonService } from './person.service';
import { ProjectService } from './project.service';
import { CopilotMeeting } from '../interfaces/copilot-meeting';
import { MeetingAnalysis as MeetingAnalysis } from '../interfaces/meeting-analyse';
import { SynchronizationService } from './synchronization.service';
import { NotifierService } from 'angular-notifier';
import { SyncEventType } from '../models/enums/syncEventType';
import { Message } from '../models/message';


@Injectable()
export class CopilotService {

  private copilotBaseURL: string;
  private copilotBaseURLws: string;
  private socket?: WebSocketSubject<unknown>;
  private speechRecognizer?: SpeechRecognizer;
  private subscriptions: Subscription[] = [];

  constructor(
    private messageService: MessageService,
    private notifier: NotifierService,
    private configService: ConfigLoaderService,
    private http: HttpClient,
    private personService: PersonService,
    private projectService: ProjectService,
    private sync: SynchronizationService,
  ) {
    this.copilotBaseURL = this.configService.settings.pyWallServer.url;
    // TODO don't do string replacement here but use proper json config file
    this.copilotBaseURLws = this.copilotBaseURL.replace(/http/, 'ws'); // only search for http so a potential 's' of https stays for wss
  }

  getMeetings(projectKey: string): Observable<CopilotMeeting[]> {
    this.messageService.sendDebug('CopilotService getMeetings called.');
    return this.http
      .get<CopilotMeeting[]>(`${this.copilotBaseURL}meetings/${projectKey}`)
      .pipe(
        tap(_ => this.messageService.sendTrace('try to get data from the server')),
        retry({ delay: 1000, count: 20 }),
      );
  }


  /** Returns the analysis id */
  createMeetingAnalysis(projectKey: string, sprintId: number, sessionId: string): Observable<string> {
    return this.http
      .post<{ resourceId: string }>(
      `${this.copilotBaseURL}analysis/`,
      {
        'projectKey': projectKey,
        'sprintId': sprintId,
        'sessionId': sessionId,
      })
      .pipe(
        map(it => it.resourceId),
      );
  }

  createChatAnalysis(projectKey: string, sprintId: number, messages: Message[]): Observable<any> {
    const payload = {
      project_key: projectKey,
      sprint_id: sprintId,
      timestamp_triggered: new Date().toISOString(),
      messages: Message.mapMessagesForChatAnalysis(messages),
    };
    return this.http
      .post<any>(
      `${this.copilotBaseURL}chat-analysis/`,
      payload,
      { observe: 'response' }
    );
  }


  getChatAnalysis(id: string): Observable<any>{
    return this.http
      .get<any>(this.copilotBaseURL + `chat-analysis/${id}`).pipe(retry({ delay: 1000, count: 200 }));
  }

  getMeetingAnalysis(id: string): Observable<MeetingAnalysis> {
    return this.http
      .get<MeetingAnalysis>(`${this.copilotBaseURL}analysis/${id}`)
      .pipe(
        retry({ delay: 2000, count: 20 }),
      );
  }

  addProjectPhrases(phraseList: PhraseListGrammar, projectKey: string): void {
    this.personService.getMembers(projectKey).pipe(
      mergeMap(members => members),
      map(member => phraseList.addPhrase(member.displayName)),
    ).subscribe();

    this.projectService.getKeysOfAvailableProjects().pipe(
      mergeMap(projectKeys => projectKeys),
      mergeMap(project => this.projectService.getProjectDetails(project).pipe(
        catchError(() => of(null))
      )),
      filter(project => project !== null),
      map(project => phraseList.addPhrase(project.name)),
    ).subscribe();
  }

  connect(
    localStream: MediaStream | null,
    projectKey: string,
    clientId: string
  ): void {
    this.sync.joinCopilotRoom(projectKey);
    this.sync.subscribeEvent<{ copilotSessionId: string }>(SyncEventType.COPILOT_SESSION_ID)
      .subscribe(event => {
        const { copilotSessionId } = event;
        this.messageService.sendInfo(`received copilot session id ${copilotSessionId}`);

        this.configService.getSpeechServerToken().subscribe(({ token, region }) => {
          const speechConfig = SpeechConfig.fromAuthorizationToken(token, region);
          speechConfig.speechRecognitionLanguage = 'de-ch'; // TODO don't hard code language

          const socket = this.initializeWebSocket();

          if (localStream) {
            const audioConfig = AudioConfig.fromStreamInput(localStream);
            this.speechRecognizer = new SpeechRecognizer(speechConfig, audioConfig);
            const phaseList = PhraseListGrammar.fromRecognizer(this.speechRecognizer);
            this.addProjectPhrases(phaseList, projectKey);

            const startTime = Date.now();
            this.speechRecognizer.recognized = (_, { offset, result }) => {
              if (result.text.length > 0) {
                this.messageService.sendInfo(`recognized "${result.text}"`);
                this.sendWebSocketMessage(socket, projectKey, clientId, copilotSessionId, result.text, startTime + offset / 10_000);
              }
            };

            this.speechRecognizer.startContinuousRecognitionAsync(() => {
              this.notifier.notify('info', 'start speech recognition');
            });
          }
        });
      });
  }

  disconnect(): void {
    // TODO: disable copoilot switch when video room is closed
    this.messageService.sendInfo('disconnect from copilot');
    this.speechRecognizer?.stopContinuousRecognitionAsync(() => {
      this.notifier.notify('info', 'stop speech recognition');
      this.speechRecognizer.close();
    }); // TODO is this the correct way to terminate recognition?

    this.socket?.complete();
    for (const ev of this.subscriptions) {
      ev.unsubscribe();
    }
    this.sync.leaveCopilotRoom();
  }

  private initializeWebSocket(): WebSocketSubject<unknown> {
    const socket = webSocket(`${this.copilotBaseURLws}upload`);
    this.subscriptions.push(socket.subscribe());
    return socket;
  }

  private sendWebSocketMessage(
    socket: WebSocketSubject<unknown>,
    projectKey: string,
    clientId: string,
    sessionId: string,
    text: string | null,
    timestamp: number
  ): void {
    socket.next({
      projectKey: projectKey,
      clientId: clientId,
      time: new Date(timestamp).toISOString(),
      text: text,
      sessionId: sessionId,
    });
  }
}
